/**
 * SASADM-0075 — per-tenant Microsoft Entra SSO config surface (super-admin only).
 *
 * A dedicated "Microsoft SSO" tab in the tenant workspace that composes named-field
 * UI over the EXISTING integration-settings admin client (`apiAdmin.integrationSettings`,
 * saas-auth SASATH-0067). No new backend / API module / endpoint / type — it writes the
 * fixed saas-auth contract groups and keys:
 *   - group `entra-sso` (SASATH-0105): keys `entra_tenant_id`, `client_id`,
 *     `client_secret` (secret), and optional `client_secret_expires_at` (SASATH-0111).
 *   - group `auth-policy` (SASATH-0109): key `allowed_providers` — the per-tenant
 *     allowed-providers OVERRIDE (comma-separated lowercased tokens from
 *     {password, google, microsoft}), resolved override-first / brand-fallback by the
 *     backend. This UI writes ONLY that override — NEVER `Brand.authProviders`.
 * Card 2 (SASADM-0164; saas-auth SASATH-0379) is a choice of the two redirect URIs the
 * backend supplies — System or Whitelabel — read from `apiAdmin.entraSso.getRedirectUri`
 * and saved as the non-secret key `redirect_host` on the `entra-sso` group. Nothing is
 * computed locally: no URI is built client-side, and after a save the read is re-fetched.
 *
 * SUPER_ADMIN-only, three ways: (a) the TABS entry is `superAdmin: true` so the strip
 * hides it; (b) this component guards on `!operator?.isSuperAdmin` (a direct URL nav
 * bypasses the strip); (c) any backend 403 threads to a contained "Super admin required"
 * state. Never assume gating from the tab alone.
 *
 * SECRET HYGIENE (load-bearing — mirrors TenantGhlIntegration / TenantIntegrationSettings):
 * the `client_secret` is NEVER displayed, logged, or persisted client-side. The read
 * returns only `valueSet`/`valueSuffix` (masked); the input is a write-only
 * `type="password"` field, never pre-filled, held only in transient component state
 * (never local/sessionStorage). On save the secret entry OMITS `value` when blank so the
 * backend preserves the stored secret (merge-on-absent); clearing is `deleteKey`.
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState, type FormEvent } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type {
  EntraRedirectHost,
  EntraRedirectUriDto,
  IntegrationGroup,
  IntegrationSetting,
  UpsertIntegrationGroupRequest,
} from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CopyToClipboardButton } from '@/components/CopyToClipboardButton';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { useCurrentOperator } from '@/auth/AuthContext';
import type { TenantWorkspaceLoaderData } from './TenantWorkspace';

// Fixed saas-auth contract constants — MUST match SASATH-0105 / 0109 / 0111 exactly.
const ENTRA_GROUP = 'entra-sso';
const AUTH_POLICY_GROUP = 'auth-policy';
const ALLOWED_PROVIDERS_KEY = 'allowed_providers';
const KEY_TENANT_ID = 'entra_tenant_id';
const KEY_CLIENT_ID = 'client_id';
const KEY_CLIENT_SECRET = 'client_secret';
const KEY_SECRET_EXPIRES_AT = 'client_secret_expires_at';
const KEY_REDIRECT_HOST = 'redirect_host';
const KEY_DOMAIN_HINT = 'domain_hint';
const KEY_JIT_PROVISIONING = 'jit_provisioning';
const JIT_ON = 'CUSTOMER';
const JIT_OFF = 'OFF';
const PROVIDERS = ['password', 'google', 'microsoft'] as const;
const HOST_OPTIONS = ['SYSTEM', 'WHITELABEL'] as const;

type Provider = (typeof PROVIDERS)[number];

function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

/** Read one key (masked) off a group; undefined when the group/key is absent. */
function findKey(group: IntegrationGroup | null, key: string): IntegrationSetting | undefined {
  return group?.keys.find((k) => k.key === key);
}

/**
 * The stored per-tenant allowed-providers override, or null when there is no override
 * (→ the tenant inherits its brand default). Displays the STORED override only — the
 * backend's `resolveByTenant` (SASATH-0109) computes the effective brand-fallback set.
 */
function readOverrideTokens(group: IntegrationGroup | null): string[] | null {
  const override = findKey(group, ALLOWED_PROVIDERS_KEY);
  return override && !override.isSecret
    ? override.value
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
    : null;
}

export function TenantEntraSso() {
  const data = useRouteLoaderData('tenant-workspace') as TenantWorkspaceLoaderData;
  const tenantId = data.tenant.id;
  const { t } = useCopy();
  const snack = useSnackbar();
  const operator = useCurrentOperator();

  // `undefined` = not yet loaded; `null` = loaded, group absent; a group = present.
  const [entraGroup, setEntraGroup] = useState<IntegrationGroup | null | undefined>(undefined);
  const [authPolicyGroup, setAuthPolicyGroup] = useState<IntegrationGroup | null | undefined>(
    undefined,
  );
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  // Card 1 form state. The secret field is ALWAYS blank (write-only, never seeded).
  const [tenantIdField, setTenantIdField] = useState('');
  const [clientIdField, setClientIdField] = useState('');
  const [expiresAtField, setExpiresAtField] = useState('');
  const [clientSecretField, setClientSecretField] = useState('');
  const [domainHintField, setDomainHintField] = useState('');
  const [domainHintError, setDomainHintError] = useState<string | null>(null);
  const [jitEnabled, setJitEnabled] = useState(false);
  const [jitError, setJitError] = useState<string | null>(null);
  const [confirmJit, setConfirmJit] = useState(false);
  const [entraSaveError, setEntraSaveError] = useState<ApiError | null>(null);
  const [entraValidationError, setEntraValidationError] = useState<string | null>(null);
  const [entraBusy, setEntraBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  // Card 3 form state. Checkboxes seeded from the stored override (all unchecked when none).
  const [selectedProviders, setSelectedProviders] = useState<Record<Provider, boolean>>({
    password: false,
    google: false,
    microsoft: false,
  });
  const [overrideSaveError, setOverrideSaveError] = useState<ApiError | null>(null);
  const [overrideLockoutError, setOverrideLockoutError] = useState<string | null>(null);
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [confirmLockout, setConfirmLockout] = useState(false);

  // Card 2 state. The read endpoint is authoritative; `redirectInfo` is never edited locally.
  const [redirectInfo, setRedirectInfo] = useState<EntraRedirectUriDto | null | undefined>(
    undefined,
  ); // undefined = loading, null = load failed
  const [selectedHost, setSelectedHost] = useState<EntraRedirectHost>('SYSTEM');
  const [redirectSaveError, setRedirectSaveError] = useState<ApiError | null>(null);
  const [redirectBusy, setRedirectBusy] = useState(false);

  // Seed Card 1 fields from the loaded/updated `entra-sso` group; the secret is re-blanked.
  function syncEntraForm(group: IntegrationGroup | null) {
    const tid = findKey(group, KEY_TENANT_ID);
    const cid = findKey(group, KEY_CLIENT_ID);
    const exp = findKey(group, KEY_SECRET_EXPIRES_AT);
    setTenantIdField(tid && !tid.isSecret ? tid.value : '');
    setClientIdField(cid && !cid.isSecret ? cid.value : '');
    setExpiresAtField(exp && !exp.isSecret ? exp.value : '');
    setClientSecretField('');
    const hint = findKey(group, KEY_DOMAIN_HINT);
    setDomainHintField(hint && !hint.isSecret ? hint.value : '');
    setDomainHintError(null);
    // SASADM-0167 — absent, OFF, or any other stored value → off.
    const jit = findKey(group, KEY_JIT_PROVISIONING);
    setJitEnabled(!!jit && !jit.isSecret && jit.value === JIT_ON);
    setJitError(null);
  }

  // Seed Card 3 checkboxes from the stored `allowed_providers` override.
  function syncOverrideForm(group: IntegrationGroup | null) {
    const tokens = readOverrideTokens(group);
    setSelectedProviders({
      password: tokens ? tokens.includes('password') : false,
      google: tokens ? tokens.includes('google') : false,
      microsoft: tokens ? tokens.includes('microsoft') : false,
    });
  }

  async function load() {
    setLoadError(null);
    try {
      const groups = await apiAdmin.integrationSettings.listGroups(tenantId);
      const entra = groups.find((g) => g.group === ENTRA_GROUP) ?? null;
      const policy = groups.find((g) => g.group === AUTH_POLICY_GROUP) ?? null;
      setEntraGroup(entra);
      setAuthPolicyGroup(policy);
      syncEntraForm(entra);
      syncOverrideForm(policy);
      setForbidden(false);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setEntraGroup(null);
        setAuthPolicyGroup(null);
        return;
      }
      if (err instanceof ApiError) setLoadError(err);
      setEntraGroup(null);
      setAuthPolicyGroup(null);
    }
  }

  async function loadRedirectUri() {
    try {
      const info = await apiAdmin.entraSso.getRedirectUri(tenantId);
      setRedirectInfo(info);
      setSelectedHost(info.redirectHost);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        return;
      }
      setRedirectInfo(null);
    }
  }

  useEffect(() => {
    if (!operator?.isSuperAdmin) return;
    // `load()` only setStates after an `await`; wrap in an inline async IIFE so the
    // effect body holds no synchronous setState (behavior identical).
    void (async () => {
      await load();
      await loadRedirectUri();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, operator]);

  // Central upsert helper: PUTs the group, applies the masked response, and threads
  // 403 → contained forbidden, other ApiErrors → the passed banner setter.
  async function runUpsert(
    group: string,
    req: UpsertIntegrationGroupRequest,
    apply: (updated: IntegrationGroup) => void,
    setError: (e: ApiError | null) => void,
    setBusy: (b: boolean) => void,
  ): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      const updated = await apiAdmin.integrationSettings.upsertGroup(tenantId, group, req);
      apply(updated);
      return true;
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        return false;
      }
      if (err instanceof ApiError) setError(err);
      else snack.error(t('errors.generic', 'Something went wrong.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  // --- Card 1: entra-sso -----------------------------------------------------

  const secretSetting = findKey(entraGroup ?? null, KEY_CLIENT_SECRET);
  const secretValueSet = !!(secretSetting && secretSetting.isSecret && secretSetting.valueSet);
  const secretMaskedLabel =
    secretSetting && secretSetting.isSecret && secretSetting.valueSet
      ? secretSetting.valueSuffix
        ? t('entraSso.secret.masked', '•••• {{suffix}}', { suffix: secretSetting.valueSuffix })
        : t('entraSso.secret.set', 'Set')
      : t('entraSso.secret.notSet', 'Not set');
  const expiryStored = !!findKey(entraGroup ?? null, KEY_SECRET_EXPIRES_AT);

  // Derivable entirely from the masked read — no secret value needed.
  const tid = findKey(entraGroup ?? null, KEY_TENANT_ID);
  const cid = findKey(entraGroup ?? null, KEY_CLIENT_ID);
  const sec = findKey(entraGroup ?? null, KEY_CLIENT_SECRET);
  const entraComplete =
    !!(tid && !tid.isSecret && tid.value.trim()) &&
    !!(cid && !cid.isSecret && cid.value.trim()) &&
    !!(sec && sec.isSecret && sec.valueSet);
  const jitStoredOn = (() => {
    const k = findKey(entraGroup ?? null, KEY_JIT_PROVISIONING);
    return !!k && !k.isSecret && k.value === JIT_ON;
  })();
  const jitStored = !!findKey(entraGroup ?? null, KEY_JIT_PROVISIONING);

  async function handleSaveEntra(e: FormEvent) {
    e.preventDefault();
    setEntraValidationError(null);
    setDomainHintError(null);
    setJitError(null);
    const trimmedTenant = tenantIdField.trim();
    const trimmedClient = clientIdField.trim();
    const trimmedSecret = clientSecretField.trim();
    if (trimmedTenant.length === 0) {
      setEntraValidationError(
        t('entraSso.errors.tenantIdRequired', 'The directory (tenant) ID is required.'),
      );
      return;
    }
    if (trimmedClient.length === 0) {
      setEntraValidationError(
        t('entraSso.errors.clientIdRequired', 'The application (client) ID is required.'),
      );
      return;
    }
    if (!secretValueSet && trimmedSecret.length === 0) {
      setEntraValidationError(
        t('entraSso.errors.secretRequired', 'A client secret is required to create the configuration.'),
      );
      return;
    }

    // SASADM-0167 — turning JIT on opens the employee app to the whole directory: confirm first.
    if (jitEnabled && !jitStoredOn) {
      setConfirmJit(true);
      return;
    }
    await submitEntra();
  }

  async function submitEntra() {
    const trimmedTenant = tenantIdField.trim();
    const trimmedClient = clientIdField.trim();
    const trimmedSecret = clientSecretField.trim();
    const entries: UpsertIntegrationGroupRequest['entries'] = [
      { key: KEY_TENANT_ID, isSecret: false, value: trimmedTenant },
      { key: KEY_CLIENT_ID, isSecret: false, value: trimmedClient },
      // Secret: send the typed value only; OMIT `value` when blank to preserve.
      trimmedSecret.length > 0
        ? { key: KEY_CLIENT_SECRET, isSecret: true, value: clientSecretField }
        : { key: KEY_CLIENT_SECRET, isSecret: true },
    ];
    // Expiry: include only when non-blank (the separate "Clear expiry" deletes it).
    if (expiresAtField.trim().length > 0) {
      entries.push({ key: KEY_SECRET_EXPIRES_AT, isSecret: false, value: expiresAtField });
    }
    // SASADM-0166 (SASATH-0380) — send domain_hint when set, or when clearing a stored value.
    // Never send a blank for a tenant that has none (keeps the write identical for them).
    const trimmedHint = domainHintField.trim();
    const hintStored = !!findKey(entraGroup ?? null, KEY_DOMAIN_HINT);
    if (trimmedHint.length > 0 || hintStored) {
      entries.push({ key: KEY_DOMAIN_HINT, isSecret: false, value: trimmedHint });
    }
    // SASADM-0167 (SASATH-0381) — absent means OFF: send only when on, or when turning a stored value off.
    if (jitEnabled || jitStored) {
      entries.push({ key: KEY_JIT_PROVISIONING, isSecret: false, value: jitEnabled ? JIT_ON : JIT_OFF });
    }

    const ok = await runUpsert(
      ENTRA_GROUP,
      { active: true, entries },
      (updated) => {
        setEntraGroup(updated);
        syncEntraForm(updated); // re-blanks the write-only secret field
      },
      (err: ApiError | null) => {
        if (err && err.code === 'VALIDATION_FAILED' && err.details.message?.includes('jit_provisioning')) {
          setJitError(err.details.message);
          setEntraSaveError(null);
          return;
        }
        if (err && err.code === 'VALIDATION_FAILED' && err.details.message?.includes('domain_hint')) {
          setDomainHintError(err.details.message);
          setEntraSaveError(null);
        } else {
          setEntraSaveError(err);
        }
      },
      setEntraBusy,
    );
    if (ok) snack.success(t('entraSso.saved', 'Microsoft SSO configuration saved.'));
  }

  async function handleClearExpiry() {
    setEntraBusy(true);
    setEntraSaveError(null);
    try {
      await apiAdmin.integrationSettings.deleteKey(tenantId, ENTRA_GROUP, KEY_SECRET_EXPIRES_AT);
      setEntraGroup((prev) =>
        prev ? { ...prev, keys: prev.keys.filter((k) => k.key !== KEY_SECRET_EXPIRES_AT) } : prev,
      );
      setExpiresAtField('');
      snack.success(t('entraSso.expiryCleared', 'Secret expiry cleared.'));
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        return;
      }
      if (err instanceof ApiError) setEntraSaveError(err);
      else snack.error(t('errors.generic', 'Something went wrong.'));
    } finally {
      setEntraBusy(false);
    }
  }

  async function handleRemoveEntra() {
    setEntraBusy(true);
    setEntraSaveError(null);
    try {
      await apiAdmin.integrationSettings.deleteGroup(tenantId, ENTRA_GROUP);
      setEntraGroup(null);
      syncEntraForm(null);
      setConfirmRemove(false);
      snack.success(t('entraSso.removed', 'Microsoft SSO configuration removed.'));
      // Deleting the group deletes `redirect_host` too — the choice reverts server-side.
      await loadRedirectUri();
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setConfirmRemove(false);
        return;
      }
      if (err instanceof ApiError) setEntraSaveError(err);
      else snack.error(t('errors.generic', 'Something went wrong.'));
      setConfirmRemove(false);
    } finally {
      setEntraBusy(false);
    }
  }

  // --- Card 2: redirect URI choice (redirect_host) ---------------------------

  async function handleSaveRedirect() {
    if (!redirectInfo || selectedHost === redirectInfo.redirectHost) return;
    const ok = await runUpsert(
      ENTRA_GROUP,
      { active: true, entries: [{ key: KEY_REDIRECT_HOST, isSecret: false, value: selectedHost }] },
      (updated) => setEntraGroup(updated),
      setRedirectSaveError,
      setRedirectBusy,
    );
    if (ok) {
      snack.success(t('entraSso.card2.saved', 'Redirect URI choice saved.'));
      await loadRedirectUri();
    }
  }

  // --- Card 3: auth-policy / allowed_providers override ----------------------

  const selected = PROVIDERS.filter((p) => selectedProviders[p]);
  const selectedExcludesPassword = selected.length > 0 && !selected.includes('password');
  const storedTokens = readOverrideTokens(authPolicyGroup ?? null);
  const hasOverride = storedTokens !== null;
  const storedExcludesPassword = hasOverride && !storedTokens.includes('password');
  const showLockoutWarning = selectedExcludesPassword || storedExcludesPassword;

  async function writeOverride(providers: string[]) {
    const ok = await runUpsert(
      AUTH_POLICY_GROUP,
      {
        active: true,
        entries: [{ key: ALLOWED_PROVIDERS_KEY, isSecret: false, value: providers.join(',') }],
      },
      (updated) => {
        setAuthPolicyGroup(updated);
        syncOverrideForm(updated);
      },
      setOverrideSaveError,
      setOverrideBusy,
    );
    if (ok) snack.success(t('entraSso.overrideSaved', 'Login methods override saved.'));
  }

  async function handleClearOverride() {
    setOverrideBusy(true);
    setOverrideSaveError(null);
    setOverrideLockoutError(null);
    try {
      await apiAdmin.integrationSettings.deleteKey(tenantId, AUTH_POLICY_GROUP, ALLOWED_PROVIDERS_KEY);
      setAuthPolicyGroup((prev) =>
        prev ? { ...prev, keys: prev.keys.filter((k) => k.key !== ALLOWED_PROVIDERS_KEY) } : prev,
      );
      syncOverrideForm(null);
      snack.success(
        t('entraSso.overrideCleared', "Override cleared — this tenant now inherits its brand's login methods."),
      );
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        return;
      }
      if (err instanceof ApiError) setOverrideSaveError(err);
      else snack.error(t('errors.generic', 'Something went wrong.'));
    } finally {
      setOverrideBusy(false);
    }
  }

  function handleSaveOverride() {
    setOverrideLockoutError(null);
    const providers = PROVIDERS.filter((p) => selectedProviders[p]);
    // Empty selection = clear the override (empty means inherit, per SASATH-0109 B3).
    if (providers.length === 0) {
      void handleClearOverride();
      return;
    }
    const excludesPassword = !providers.includes('password');
    // H1 guard (load-bearing): forcing Microsoft-only with an incomplete entra-sso
    // config would lock the whole tenant out — block with an inline error, no network.
    if (excludesPassword && providers.includes('microsoft') && !entraComplete) {
      setOverrideLockoutError(
        t(
          'entraSso.errors.lockoutIncomplete',
          'Complete the Microsoft SSO configuration (directory ID, application ID, client secret) before forcing Microsoft-only, or every user in this tenant will be locked out.',
        ),
      );
      return;
    }
    // Excludes password with a complete config → confirm the lockout risk before writing.
    if (excludesPassword && entraComplete) {
      setConfirmLockout(true);
      return;
    }
    void writeOverride(providers);
  }

  // --- Render ----------------------------------------------------------------

  // (b) Component-level super-admin guard — a direct URL nav bypasses the tab strip.
  if (!operator?.isSuperAdmin) {
    return (
      <Alert severity="warning">
        {t('entraSso.superAdminRequired', 'Super admin required to manage Microsoft SSO.')}
      </Alert>
    );
  }

  const title = (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h2" component="h2" gutterBottom>
        {t('entraSso.title', 'Microsoft SSO')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t(
          'entraSso.intro',
          "How this tenant's users sign in — Microsoft Entra (Azure AD) single sign-on. Configure the customer's registration, the redirect URI they register, and this tenant's login-method override.",
        )}
      </Typography>
    </Box>
  );

  if (forbidden) {
    return (
      <Box>
        {title}
        <Alert severity="warning">
          {t('entraSso.superAdminRequired', 'Super admin required to manage Microsoft SSO.')}
        </Alert>
      </Box>
    );
  }

  if (entraGroup === undefined) {
    return (
      <Box>
        {title}
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <CircularProgress size={16} />
          <Typography sx={{ color: 'text.secondary' }}>{t('common.loading', 'Loading')}</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      {title}
      <ErrorBanner error={loadError} />
      <Stack spacing={3}>
        {/* Card 1 — Microsoft SSO configuration (entra-sso). */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h3" component="h3" gutterBottom>
            {t('entraSso.card1.title', 'Microsoft SSO configuration')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {t(
              'entraSso.card1.explanation',
              "Enter the values from the customer's Azure app registration. The client secret is write-only — after saving it is only ever shown as a masked suffix.",
            )}
          </Typography>
          <ErrorBanner message={entraValidationError} error={entraSaveError} />
          <Box component="form" onSubmit={(e) => void handleSaveEntra(e)} noValidate>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('entraSso.fields.tenantId', 'Directory (tenant) ID')}
                  value={tenantIdField}
                  onChange={(e) => setTenantIdField(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('entraSso.fields.clientId', 'Application (client) ID')}
                  value={clientIdField}
                  onChange={(e) => setClientIdField(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={1}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t('entraSso.secretStatus', 'Stored secret: {{state}}', {
                      state: secretMaskedLabel,
                    })}
                    sx={{ alignSelf: 'flex-start' }}
                  />
                  <TextField
                    fullWidth
                    type="password"
                    label={t('entraSso.fields.clientSecret', 'Client secret')}
                    value={clientSecretField}
                    onChange={(e) => setClientSecretField(e.target.value)}
                    autoComplete="new-password"
                    required={!secretValueSet}
                    helperText={t(
                      'entraSso.fields.clientSecretHelper',
                      'Write-only — shown only as a masked suffix after saving. Leave blank to keep the current secret.',
                    )}
                  />
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label={t('entraSso.fields.expiresAt', 'Client secret expiry (optional)')}
                  value={expiresAtField}
                  onChange={(e) => setExpiresAtField(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  helperText={t(
                    'entraSso.fields.expiresAtHelper',
                    "Optional. From the Azure secret's expiry; drives the platform expiry warning.",
                  )}
                />
                {expiryStored ? (
                  <Button size="small" disabled={entraBusy} onClick={() => void handleClearExpiry()}>
                    {t('entraSso.clearExpiry', 'Clear expiry')}
                  </Button>
                ) : null}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('entraSso.fields.domainHint', 'Domain hint (optional)')}
                  placeholder="contoso.com"
                  value={domainHintField}
                  onChange={(e) => {
                    setDomainHintField(e.target.value);
                    setDomainHintError(null);
                  }}
                  error={domainHintError !== null}
                  helperText={
                    domainHintError ??
                    t(
                      'entraSso.fields.domainHintHelper',
                      "Sends users straight to this organization's Microsoft sign-in. Invitations to addresses on this domain are pre-authorized: no email is sent, and they activate on the person's first Microsoft sign-in. Other addresses get a normal invitation email.",
                    )
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl error={jitError !== null}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={jitEnabled}
                        onChange={(e) => {
                          setJitEnabled(e.target.checked);
                          setJitError(null);
                        }}
                      />
                    }
                    label={t(
                      'entraSso.jit.label',
                      'Automatically add people from this Microsoft directory',
                    )}
                  />
                  <FormHelperText>
                    {jitError ??
                      t(
                        'entraSso.jit.helper',
                        "Anyone who can sign in with this tenant's Microsoft directory, including its guests, gets access to the employee app on their first sign-in. The admin app stays by invitation; to pre-place an admin, invite them (an address on the domain hint needs no email). To block someone, suspend them; removing them lets them back in on their next sign-in.",
                      )}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={entraBusy}
                startIcon={entraBusy ? <CircularProgress size={16} /> : null}
              >
                {t('common.save', 'Save')}
              </Button>
              {entraGroup ? (
                <Button color="error" disabled={entraBusy} onClick={() => setConfirmRemove(true)}>
                  {t('entraSso.remove', 'Remove Microsoft SSO config')}
                </Button>
              ) : null}
            </Stack>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>
            {t(
              'entraSso.card1.runbookNote',
              'Customer onboarding steps: docs/runbooks/entra-sso-customer-onboarding.md',
            )}
          </Typography>
        </Paper>

        {/* Card 2 — Redirect URI to register in Azure: System or Whitelabel (SASATH-0379). */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h3" component="h3" gutterBottom>
            {t('entraSso.card2.title', 'Redirect URI to register in Azure')}
          </Typography>
          {redirectInfo === undefined ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <CircularProgress size={16} />
              <Typography sx={{ color: 'text.secondary' }}>
                {t('common.loading', 'Loading')}
              </Typography>
            </Stack>
          ) : null}
          {redirectInfo === null ? (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setRedirectInfo(undefined);
                    void loadRedirectUri();
                  }}
                >
                  {t('entraSso.card2.retry', 'Retry')}
                </Button>
              }
            >
              {t('entraSso.card2.loadError', "Couldn't load the redirect URI")}
            </Alert>
          ) : null}
          {redirectInfo ? (
            <>
              <ErrorBanner error={redirectSaveError} />
              {redirectInfo.options[redirectInfo.redirectHost] === null ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t(
                    'entraSso.card2.currentUnavailable',
                    'The current choice has no available host. Choose System.',
                  )}
                </Alert>
              ) : null}
              <FormControl component="fieldset" fullWidth>
                <FormLabel id="entra-redirect-host-label">
                  {t('entraSso.card2.choiceLabel', 'Redirect URI to use')}
                </FormLabel>
                <RadioGroup
                  aria-labelledby="entra-redirect-host-label"
                  value={selectedHost}
                  onChange={(e) => setSelectedHost(e.target.value as EntraRedirectHost)}
                >
                  {HOST_OPTIONS.map((host) => {
                    const uri = redirectInfo.options[host];
                    const isSystem = host === 'SYSTEM';
                    return (
                      <Box key={host} sx={{ mt: 1 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <FormControlLabel
                            value={host}
                            control={<Radio />}
                            disabled={uri === null}
                            label={
                              isSystem
                                ? t('entraSso.card2.options.system', 'System')
                                : t('entraSso.card2.options.whitelabel', 'Whitelabel')
                            }
                          />
                          {redirectInfo.redirectHost === host ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t('entraSso.card2.current', 'Current')}
                            />
                          ) : null}
                        </Stack>
                        {uri !== null ? (
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <TextField
                              fullWidth
                              size="small"
                              label={
                                isSystem
                                  ? t('entraSso.card2.fieldLabel.system', 'System redirect URI')
                                  : t(
                                      'entraSso.card2.fieldLabel.whitelabel',
                                      'Whitelabel redirect URI',
                                    )
                              }
                              value={uri}
                              slotProps={{
                                input: { readOnly: true, sx: { fontFamily: 'monospace' } },
                              }}
                            />
                            <CopyToClipboardButton
                              value={uri}
                              ariaLabel={
                                isSystem
                                  ? t('entraSso.card2.copyAria.system', 'Copy System redirect URI')
                                  : t(
                                      'entraSso.card2.copyAria.whitelabel',
                                      'Copy Whitelabel redirect URI',
                                    )
                              }
                            />
                          </Stack>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {isSystem
                              ? t('entraSso.card2.systemUnavailable', 'Not available')
                              : t(
                                  'entraSso.card2.whitelabelUnavailable',
                                  'Activate a white-label domain with an auth host first',
                                )}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </RadioGroup>
              </FormControl>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                {t(
                  'entraSso.card2.instruction',
                  'Register the selected URI in Azure as a Web redirect URI. To switch, add the other URI in Azure first (Azure allows several), then switch here.',
                )}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  disabled={
                    redirectBusy ||
                    selectedHost === redirectInfo.redirectHost ||
                    redirectInfo.options[selectedHost] === null
                  }
                  startIcon={redirectBusy ? <CircularProgress size={16} /> : null}
                  onClick={() => void handleSaveRedirect()}
                >
                  {t('entraSso.card2.save', 'Save redirect choice')}
                </Button>
              </Stack>
            </>
          ) : null}
        </Paper>

        {/* Card 3 — Login methods for this tenant (auth-policy override). */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h3" component="h3" gutterBottom>
            {t('entraSso.card3.title', 'Login methods for this tenant')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {t(
              'entraSso.card3.explanation',
              'Set a per-tenant override of the login methods this tenant may use. This overrides only this tenant; it never changes the shared brand. Clear it to inherit the brand default.',
            )}
          </Typography>
          {!hasOverride ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t(
                'entraSso.card3.noOverride',
                "No per-tenant override — this tenant inherits its brand's login methods.",
              )}
            </Alert>
          ) : null}
          <ErrorBanner message={overrideLockoutError} error={overrideSaveError} />
          {showLockoutWarning ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t(
                'entraSso.card3.lockoutWarning',
                'This tenant cannot use password login. If Microsoft SSO breaks or the client secret expires, users can be locked out; recovery requires a platform super-admin break-glass (SASATH-0111).',
              )}
            </Alert>
          ) : null}
          <FormGroup>
            {PROVIDERS.map((p) => (
              <FormControlLabel
                key={p}
                control={
                  <Checkbox
                    checked={selectedProviders[p]}
                    onChange={(e) =>
                      setSelectedProviders((prev) => ({ ...prev, [p]: e.target.checked }))
                    }
                  />
                }
                label={t(`entraSso.providers.${p}`, p)}
              />
            ))}
          </FormGroup>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mt: 2 }}>
            <Button
              variant="contained"
              disabled={overrideBusy}
              startIcon={overrideBusy ? <CircularProgress size={16} /> : null}
              onClick={() => handleSaveOverride()}
            >
              {t('entraSso.card3.save', 'Save login methods')}
            </Button>
            {hasOverride ? (
              <Button disabled={overrideBusy} onClick={() => void handleClearOverride()}>
                {t('entraSso.card3.clear', 'Clear override (inherit brand default)')}
              </Button>
            ) : null}
          </Stack>
        </Paper>
      </Stack>

      <ConfirmDialog
        open={confirmRemove}
        title={t('entraSso.removeTitle', 'Remove Microsoft SSO configuration?')}
        body={t(
          'entraSso.removeBody',
          "This deletes the tenant's Microsoft Entra SSO configuration (directory ID, application ID, and client secret). If this tenant is set to Microsoft-only login, users will be unable to sign in until it is reconfigured; a platform super-admin can temporarily re-enable password login as a break-glass recovery (SASATH-0111). This cannot be undone.",
        )}
        severity="error"
        confirmLabel={t('entraSso.remove', 'Remove Microsoft SSO config')}
        busy={entraBusy}
        onConfirm={() => void handleRemoveEntra()}
        onCancel={() => setConfirmRemove(false)}
      />

      <ConfirmDialog
        open={confirmLockout}
        title={t('entraSso.confirmLockoutTitle', 'Force non-password login for this tenant?')}
        body={t(
          'entraSso.confirmLockoutBody',
          'This tenant will no longer allow password login. If Microsoft SSO breaks or the client secret expires, all users can be locked out; recovery requires a platform super-admin break-glass (SASATH-0111). Continue?',
        )}
        severity="warning"
        confirmLabel={t('entraSso.confirmLockoutConfirm', 'Force these login methods')}
        busy={overrideBusy}
        onConfirm={() => {
          setConfirmLockout(false);
          void writeOverride(PROVIDERS.filter((p) => selectedProviders[p]));
        }}
        onCancel={() => setConfirmLockout(false)}
      />

      <ConfirmDialog
        open={confirmJit}
        title={t('entraSso.jit.confirmTitle', 'Let everyone in this Microsoft directory in?')}
        body={t(
          'entraSso.jit.confirmBody',
          "Anyone who can sign in with this tenant's Microsoft directory, including its guests, will get access to the employee app on their first sign-in, without an invitation.",
        )}
        severity="warning"
        confirmLabel={t('entraSso.jit.confirmAction', 'Turn on and save')}
        busy={entraBusy}
        onConfirm={() => {
          setConfirmJit(false);
          void submitEntra();
        }}
        onCancel={() => setConfirmJit(false)}
      />
    </Box>
  );
}
